"use client";

import * as React from "react";
import { 
  MoreHorizontal, 
  Shield, 
  User, 
  UserMinus, 
  Mail, 
  XCircle,
  Clock
} from "lucide-react";
import { useOrganization } from "../../lib/organization/OrganizationProvider.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu.js";
import { Button } from "../button.js";
import { Badge } from "../badge.js";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip.js";
import { useSimpleAuth } from "../../lib/auth/SimpleAuthProvider.js";

export function TeamMembersList() {
  const { 
    members, 
    invitations, 
    isLoading, 
    isOrgAdmin, 
    removeMember, 
    updateMemberRole,
    cancelInvitation
  } = useOrganization();
  const { user } = useSimpleAuth();

  if (isLoading && members.length === 0) {
    return <div className="p-8 text-center">Loading team members...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined At</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.user_id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <div className="font-medium">
                        {member.user?.first_name} {member.user?.last_name}
                        {member.user_id === user?.id && " (You)"}
                      </div>
                      <div className="text-xs text-slate-500">{member.user?.email}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={member.role === 'admin' ? "default" : "secondary"}>
                    {member.role === 'admin' && <Shield className="mr-1 h-3 w-3" />}
                    {member.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-slate-500">
                  {new Date(member.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {isOrgAdmin && member.user_id !== user?.id && (
                    <DropdownMenu>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0" aria-label="Open member actions menu">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent>Open member actions menu</TooltipContent>
                      </Tooltip>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem 
                          onClick={() => updateMemberRole(member.user_id, member.role === 'admin' ? 'member' : 'admin')}
                        >
                          Change to {member.role === 'admin' ? 'Member' : 'Admin'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => removeMember(member.user_id)}
                        >
                          <UserMinus className="mr-2 h-4 w-4" />
                          Remove Member
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {invitations.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Pending Invitations
          </h3>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell className="font-medium">{invite.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{invite.role}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(invite.expires_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {isOrgAdmin && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 h-8 w-8 p-0"
                              aria-label="Cancel invitation"
                              onClick={() => cancelInvitation(invite.id)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Cancel invitation</TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
